import { Container, Sprite } from 'pixi.js';
import { ANIMATION_FRAMES, PAPER_DOLL_LAYERS, } from '@enugu/shared';
import { authoredDirection, isMirrored } from '../iso/IsoMath';
/**
 * A character drawn as stacked layer sprites (shadow, body, bottom, shoes,
 * top, outer, hair, headwear, accessory, held) sharing one anchor (the feet).
 * SW/NW are SE/NE mirrored by flipping the container's x scale.
 *
 * Texture lookups happen only when the animation, authored direction or
 * outfit changes; a frame change is an array index per layer.
 */
export class PaperDoll {
    view;
    provider;
    slots = [];
    anim = 'idle';
    dir = 'SE';
    mirrored = false;
    frame = -1;
    stripsDirty = true;
    /** Bumped whenever the outfit changes: the cache key for baked composites (docs/30 §Baking). */
    outfitVersion = 0;
    constructor(provider, outfit) {
        this.provider = provider;
        this.view = new Container({ label: 'doll' });
        this.setOutfit(outfit);
    }
    /** Rebuilds layer sprites for a new outfit (clothes change). */
    setOutfit(outfit) {
        for (const s of this.slots)
            s.sprite.destroy();
        this.slots = [];
        this.view.removeChildren();
        for (const layer of PAPER_DOLL_LAYERS) {
            const variant = outfit.layers[layer];
            if (!variant)
                continue;
            const sprite = new Sprite();
            const tint = outfit.tints?.[layer];
            // Tints are only for clothing layers; skin tone comes from pre-rendered body variants.
            if (tint !== undefined && layer !== 'body')
                sprite.tint = tint;
            this.view.addChild(sprite);
            this.slots.push({ layer, variant, sprite, strip: null });
        }
        this.stripsDirty = true;
        this.frame = -1;
        this.outfitVersion++;
    }
    /** Number of sprites this doll draws (stat). */
    get spriteCount() {
        return this.slots.length;
    }
    /** Sets the pose. Cheap when nothing changed. */
    setPose(anim, facing, frame) {
        const dir = authoredDirection(facing);
        const mirrored = isMirrored(facing);
        if (anim !== this.anim || dir !== this.dir) {
            this.anim = anim;
            this.dir = dir;
            this.stripsDirty = true;
        }
        if (mirrored !== this.mirrored) {
            this.mirrored = mirrored;
            this.view.scale.x = mirrored ? -1 : 1;
        }
        if (this.stripsDirty) {
            this.stripsDirty = false;
            for (const s of this.slots) {
                s.strip = this.provider.strip(s.layer, s.variant, anim, dir);
                // Sprite only adopts a texture's defaultAnchor in its constructor, so copy it here.
                const a = s.strip?.[0]?.defaultAnchor;
                if (a)
                    s.sprite.anchor.set(a.x, a.y);
            }
            this.frame = -1;
        }
        const f = frame % ANIMATION_FRAMES[anim];
        if (f === this.frame)
            return;
        this.frame = f;
        for (const s of this.slots) {
            const strip = s.strip;
            if (!strip || strip.length === 0) {
                if (s.sprite.visible)
                    s.sprite.visible = false;
                continue;
            }
            // Single-frame layers (e.g. the shadow) reuse frame 0.
            const tex = strip[f < strip.length ? f : 0];
            if (s.sprite.texture !== tex)
                s.sprite.texture = tex;
            if (!s.sprite.visible)
                s.sprite.visible = true;
        }
    }
    destroy() {
        this.view.destroy({ children: true });
        this.slots = [];
    }
}
